import React, { useCallback, useState } from 'react';
import onboardingMessages from '@/messages/onboarding';
import OrgOnboardingPage from './OrgOnboardingPage';
import { Text, VStack } from '@chakra-ui/react';
import { useOrganizationOnboardingStore } from '@/store/useOrganizationOnboardingStore';
import TabButtons from '@/components/atoms/TabButtons';
import { FORM_FIELD_WIDTH } from '@/constants/dimensions';
import { TUserAffiliation } from '@/types/User.types';
import RosterManualEntryForm, { MANUAL_ROSTER_ITEM_DEFAULT_VALUES, TRosterUserType } from './C_RosterManualEntryForm';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import RosterCSVFile from './C_RosterCSVFile';
import { rosterUsersSchema } from './validations';

type Props = {
  onGoBack?: () => void;
  onContinue?: () => void;
};

const RosterTemplate = ({ onGoBack = () => {}, onContinue = () => {} }: Props) => {
  const {
    rosterCSVPreview,
    setRosterCSVPreview,
    rosterCSVFile,
    setRosterCSVFile,
    rosterCSV: rosterCSVFromStore,
    rosterManual: rosterManualFromStore,
    setRosterManual: setRosterManualToStore,
    setRosterCSV: setRosterCSVToStore,
    selectedRosterTab: selectedRosterTabFromStore,
    setSelectedRosterTab: setSelectedRosterTabToStore,
    CSVTabStep: csvTabStepFromStore,
    setCSVTabStep: setCsvTabStepToStore,
    peerSupportCertificateDataCSV,
    peerSupportCertificateDataManual,
    setPeerSupportCertificateDataCSV,
    setPeerSupportCertificateDataManual,
  } = useOrganizationOnboardingStore();

  const [customRosterError, setCustomRosterError] = useState('');

  const methods = useForm<{
    rosterUsers?: TRosterUserType[];
  }>({
    defaultValues: {
      rosterUsers: rosterManualFromStore ? rosterManualFromStore : [MANUAL_ROSTER_ITEM_DEFAULT_VALUES],
    },
    mode: 'all',
    reValidateMode: 'onSubmit',
    resolver: yupResolver(rosterUsersSchema),
  });

  const { formState, watch } = methods;

  const manualRosterUsers = watch('rosterUsers');

  const { isValid } = formState;

  // CSV-editable roster table data
  const methodsCSV = useForm<{
    rosterUsers?: TRosterUserType[];
  }>({
    defaultValues: {
      rosterUsers: rosterCSVFromStore ? rosterCSVFromStore : [MANUAL_ROSTER_ITEM_DEFAULT_VALUES],
    },
    mode: 'all',
    reValidateMode: 'onSubmit',
    resolver: yupResolver(rosterUsersSchema),
  });

  const { formState: formStateCSV, setValue: setValueCSV, trigger: triggerCSV, watch: watchCSV } = methodsCSV;

  const csvRosterUsers = watchCSV('rosterUsers');

  const { isValid: isValidCSV } = formStateCSV;

  const onSubmitRoster = useCallback(() => {
    if (selectedRosterTabFromStore === 'manual') {
      const mapped = mapRosterForStore(manualRosterUsers);

      setRosterManualToStore(mapped);
    } else {
      const mapped = mapRosterForStore(csvRosterUsers);

      setRosterCSVToStore(mapped);
    }

    onContinue();
  }, [selectedRosterTabFromStore, rosterCSVFile, rosterCSVPreview, manualRosterUsers, csvRosterUsers]);

  const onSubmitLocal = useCallback(() => {
    if (selectedRosterTabFromStore === 'csv') {
      if (csvTabStepFromStore === 0 && rosterCSVFromStore) {
        setValueCSV('rosterUsers', rosterCSVFromStore);
        triggerCSV('rosterUsers'); // super important!
        setCsvTabStepToStore(1);
      }
      if (csvTabStepFromStore === 1) {
        onSubmitRoster();
      }
    } else {
      onSubmitRoster();
    }
  }, [selectedRosterTabFromStore, csvTabStepFromStore, rosterCSVFromStore, onSubmitRoster]);

  const onGoBackLocal = useCallback(() => {
    if (selectedRosterTabFromStore === 'csv' && csvTabStepFromStore === 1) {
      setCsvTabStepToStore(0);
    } else {
      const mappedCSV = mapRosterForStore(csvRosterUsers);
      const mappedManual = mapRosterForStore(manualRosterUsers);

      setRosterCSVToStore(mappedCSV);
      setRosterManualToStore(mappedManual);

      onGoBack();
    }
  }, [selectedRosterTabFromStore, manualRosterUsers, csvRosterUsers]);

  return (
    <OrgOnboardingPage
      title={onboardingMessages.createYourRosterTitle}
      subTitle={onboardingMessages.createYourRosterSubtitle}
      description={
        selectedRosterTabFromStore === 'manual'
          ? onboardingMessages.createYourRosterDescriptionManual
          : onboardingMessages.createYourRosterDescriptionCSV
      }
      onGoBack={onGoBackLocal}
      onSubmit={onSubmitLocal}
      isSubmitDisabled={
        selectedRosterTabFromStore === 'manual' ? !isValid : csvTabStepFromStore === 0 ? !rosterCSVPreview : !isValidCSV
      }
    >
      <VStack marginTop={'40px'}>
        <TabButtons
          options={[
            {
              labelDisplay: 'MANUALLY ADD',
              labelValue: 'manual',
              onClick: () => setSelectedRosterTabToStore('manual'),
            },
            { labelDisplay: 'UPLOAD CSV', labelValue: 'csv', onClick: () => setSelectedRosterTabToStore('csv') },
          ]}
          selectedOptionValue={selectedRosterTabFromStore}
        />
      </VStack>

      {selectedRosterTabFromStore === 'manual' ? (
        <RosterManualEntryForm
          methods={methods}
          peerSupportData={peerSupportCertificateDataManual}
          setPeerSupportData={setPeerSupportCertificateDataManual}
        />
      ) : (
        <RosterCSVFile
          csvTabStep={csvTabStepFromStore}
          selectedFile={rosterCSVFile}
          setSelectedFile={setRosterCSVFile}
          preview={rosterCSVPreview}
          setPreview={setRosterCSVPreview}
          setRoster={setRosterCSVToStore}
          setCustomRosterError={setCustomRosterError}
          methods={methodsCSV}
          peerSupportData={peerSupportCertificateDataCSV}
          setPeerSupportData={setPeerSupportCertificateDataCSV}
        />
      )}

      {customRosterError.length > 0 && (
        <Text variant={'error'} marginTop={'10px'} width={FORM_FIELD_WIDTH}>
          {customRosterError}
        </Text>
      )}
    </OrgOnboardingPage>
  );
};

export default RosterTemplate;

const mapRosterForStore = (roster: TRosterUserType[] | undefined) =>
  (roster ?? []).map((user) => ({
    ...user,
    Affiliation: user.Affiliation as TUserAffiliation, // will be this type because we are selecting from a <Select ... />
    Invitation: '',
  })); // need to artificially add Invitation
